// 既存データからフォームデータに変換する関数
export const transformWorkHistoryToFormData = (workHistory) => {
  if (!workHistory) return {};

  const formData = {
    // ========================================
    // ステップ1: プロジェクト基本情報
    // ========================================
    name: workHistory.name || "",
    period_from: workHistory.period_from || "",
    period_at: workHistory.period_at || "",
    participation_flag:
      workHistory.participation_flag === "true" ||
      workHistory.participation_flag === true,
    project_scale: workHistory.project_scale || "",
    team_scale: workHistory.team_scale || "",
    achievement: workHistory.achievement || "",

    // ステップ1の開発プロセス（選択式）
    development_methods:
      workHistory.work_histories_development_methods?.map((dm) => ({
        id: dm.development_method?.id || dm.development_method_id || null,
        name: dm.development_method?.name || dm.development_method_name || "",
      })) || [],

    // ステップ1のスプリントの周期
    sprint_cycle_week:
      workHistory.work_histories_development_methods?.find(
        (dm) =>
          (dm.development_method?.name || dm.development_method_name) ===
          "スクラム"
      )?.sprint_cycle_week || "",

    // ステップ1の役割（選択式）
    role_ids:
      workHistory.work_histories_roles?.map((role) => role.role_id) || [],

    // ステップ1の業種（複数選択式）
    industry_ids:
      workHistory.work_history_industries?.map(
        (industry) => industry.industry_id
      ) || [],

    // ========================================
    // ステップ2-7: 各工程の作業内容（選択式 + 手動入力）
    // ========================================
    requirements_definition_operations:
      workHistory.work_history_requirements_definition_operations?.map(
        (req) => ({
          operation_id: req.requirements_definition_operation_id || "", // 選択式（ドロップダウン）
          content: req.content || "", // 手動入力（テキストエリア）
        })
      ) || [{ operation_id: "", content: "" }],

    basic_design_operations:
      workHistory.work_history_basic_design_operations?.map((design) => ({
        operation_id: design.basic_design_operation_id || "", // 選択式（ドロップダウン）
        content: design.content || "", // 手動入力（テキストエリア）
      })) || [{ operation_id: "", content: "" }],

    detail_design_operations:
      workHistory.work_history_detail_design_operations?.map((detail) => ({
        operation_id: detail.detail_design_operation_id || "", // 選択式（ドロップダウン）
        content: detail.content || "", // 手動入力（テキストエリア）
      })) || [{ operation_id: "", content: "" }],

    program_operations: workHistory.work_history_program_operations?.map(
      (program) => ({
        operation_id: program.program_operation_id || "", // 選択式（ドロップダウン）
        content: program.content || "", // 手動入力（テキストエリア）
      })
    ) || [{ operation_id: "", content: "" }],

    test_operations: workHistory.work_history_test_operations?.map((test) => ({
      operation_id: test.test_operation_id || "", // 選択式（ドロップダウン）
      content: test.content || "", // 手動入力（テキストエリア）
    })) || [{ operation_id: "", content: "" }],

    maintenance_operations:
      workHistory.work_history_maintenance_operation_operations?.map(
        (maintenance) => ({
          operation_id: maintenance.maintenance_operation_operation_id || "", // 選択式（ドロップダウン）
          content: maintenance.content || "", // 手動入力（テキストエリア）
        })
      ) || [{ operation_id: "", content: "" }],

    // ========================================
    // ステップ8-9: 言語・フレームワーク（選択式 + 手動入力）
    // ========================================
    frontend_languages: (() => {
      const frontendLangs =
        workHistory.work_histories_languages?.filter(
          (lang) => lang.language_type === "front_end"
        ) || [];
      // 言語ごとにグループ化
      const grouped = {};
      frontendLangs.forEach((lang) => {
        const key = lang.language_name || "unknown";
        if (!grouped[key]) {
          grouped[key] = {
            language_name: lang.language_name,
            language_id: lang.language_id,
            fw_library_names: [],
          };
        }
        if (lang.fw_library_name) {
          grouped[key].fw_library_names.push(lang.fw_library_name);
        }
      });

      return Object.values(grouped).map((group) => ({
        language_name: group.language_name, // 手動入力（テキストフィールド）
        language_id: group.language_id, // 選択式（ドロップダウン）の場合のID
        fw_library_name: group.fw_library_names.join(", "), // 手動入力（テキストフィールド）
        fw_library_id: "", // 選択式（ドロップダウン）の場合のID
      }));
    })(),

    backend_languages: (() => {
      const backendLangs =
        workHistory.work_histories_languages?.filter(
          (lang) => lang.language_type === "back_end"
        ) || [];
      // 言語ごとにグループ化
      const grouped = {};
      backendLangs.forEach((lang) => {
        const key = lang.language_name || "unknown";
        if (!grouped[key]) {
          grouped[key] = {
            language_name: lang.language_name,
            language_id: lang.language_id,
            fw_library_names: [],
          };
        }
        if (lang.fw_library_name) {
          grouped[key].fw_library_names.push(lang.fw_library_name);
        }
      });

      return Object.values(grouped).map((group) => ({
        language_name: group.language_name, // 手動入力（テキストフィールド）
        language_id: group.language_id, // 選択式（ドロップダウン）の場合のID
        fw_library_name: group.fw_library_names.join(", "), // 手動入力（テキストフィールド）
        fw_library_id: "", // 選択式（ドロップダウン）の場合のID
      }));
    })(),

    // ========================================
    // ステップ10: 業務システム（選択式 + 手動入力）
    // ========================================
    business_systems: (() => {
      const businessSystems = workHistory.work_histories_business_systems || [];
      // 業務システムごとにグループ化
      const grouped = {};
      businessSystems.forEach((bs) => {
        const key = bs.business_system_name || "unknown";
        if (!grouped[key]) {
          grouped[key] = {
            business_system_name: bs.business_system_name,
            business_system_id: bs.business_system_id,
            product_language_names: [],
          };
        }
        if (bs.business_system_product_language_name) {
          grouped[key].product_language_names.push(
            bs.business_system_product_language_name
          );
        }
      });

      return Object.values(grouped).map((group) => ({
        business_system_name: group.business_system_name, // 手動入力（テキストフィールド）
        business_system_id: group.business_system_id, // 選択式（ドロップダウン）の場合のID
        business_system_product_language_name:
          group.product_language_names.join(", "), // 手動入力（テキストフィールド）
        business_system_product_language_id: "", // 選択式（ドロップダウン）の場合のID
      }));
    })(),

    // ========================================
    // ステップ11: クラウドサービス（選択式 + 手動入力）
    // ========================================
    cloud_services: (() => {
      const cloudServices = workHistory.work_histories_cloud_services || [];
      // クラウドサービスごとにグループ化
      const grouped = {};
      cloudServices.forEach((cs) => {
        const key = cs.cloud_service_name || "unknown";
        if (!grouped[key]) {
          grouped[key] = {
            cloud_service_name: cs.cloud_service_name,
            cloud_service_id: cs.cloud_service_id,
            product_names: [],
          };
        }
        if (cs.cloud_service_product_name) {
          grouped[key].product_names.push(cs.cloud_service_product_name);
        }
      });

      return Object.values(grouped).map((group) => ({
        cloud_service_name: group.cloud_service_name, // 手動入力（テキストフィールド）
        cloud_service_id: group.cloud_service_id, // 選択式（ドロップダウン）の場合のID
        cloud_service_product_name: group.product_names.join(", "), // 手動入力（テキストフィールド）
        cloud_service_product_id: "", // 選択式（ドロップダウン）の場合のID
      }));
    })(),

    // ========================================
    // ステップ12: サーバーOS（選択式 + 手動入力）
    // ========================================
    server_operating_systems: (() => {
      const serverOSs =
        workHistory.work_histories_server_operating_systems || [];
      // サーバーOSごとにグループ化
      const grouped = {};
      serverOSs.forEach((sos) => {
        const key = sos.server_operating_system_name || "unknown";
        if (!grouped[key]) {
          grouped[key] = {
            server_operating_system_name: sos.server_operating_system_name,
            server_operating_system_id: sos.server_operating_system_id,
            environment_names: [],
          };
        }
        if (sos.server_environment_name) {
          grouped[key].environment_names.push(sos.server_environment_name);
        }
      });

      return Object.values(grouped).map((group) => ({
        server_operating_system_name: group.server_operating_system_name, // 手動入力（テキストフィールド）
        server_operating_system_id: group.server_operating_system_id, // 選択式（ドロップダウン）の場合のID
        server_environment_name: group.environment_names.join(", "), // 手動入力（テキストフィールド）
        server_environment_id: "", // 選択式（ドロップダウン）の場合のID
      }));
    })(),

    // ========================================
    // ステップ13: ミドルウェア（選択式 + 手動入力）
    // ========================================
    middlewares: (() => {
      const middlewares = workHistory.work_histories_middlewares || [];
      // ミドルウェアごとにグループ化
      const grouped = {};
      middlewares.forEach((mw) => {
        const key = mw.middleware_name || "unknown";
        if (!grouped[key]) {
          grouped[key] = {
            middleware_name: mw.middleware_name,
            middleware_id: mw.middleware_id,
            product_names: [],
          };
        }
        if (mw.middleware_product_name) {
          grouped[key].product_names.push(mw.middleware_product_name);
        }
      });

      return Object.values(grouped).map((group) => ({
        middleware_name: group.middleware_name, // 手動入力（テキストフィールド）
        middleware_id: group.middleware_id, // 選択式（ドロップダウン）の場合のID
        middleware_product_name: group.product_names.join(", "), // 手動入力（テキストフィールド）
        middleware_product_id: "", // 選択式（ドロップダウン）の場合のID
      }));
    })(),

    // ========================================
    // ステップ14-18: その他技術要素（手動入力用）
    // ========================================
    // 手動入力フィールド - オブジェクト形式（IDと名前のペア）
    operating_systems: workHistory.work_histories_operating_systems?.map(
      (os) => ({
        operating_system_name: os.operating_system_name, // 手動入力（テキストフィールド）
        operating_system_id: os.operating_system_id, // 選択式（ドロップダウン）の場合のID
      })
    ) || [{ operating_system_name: "", operating_system_id: "" }],

    dbs: workHistory.work_histories_dbs?.map((db) => ({
      db_name: db.db_name, // 手動入力（テキストフィールド）
      db_id: db.db_id, // 選択式（ドロップダウン）の場合のID
    })) || [{ db_name: "", db_id: "" }],

    networks: workHistory.work_histories_networks?.map((network) => ({
      network_name: network.network_name, // 手動入力（テキストフィールド）
      network_id: network.network_id, // 選択式（ドロップダウン）の場合のID
    })) || [{ network_name: "", network_id: "" }],

    design_tools: workHistory.work_histories_design_tools?.map((tool) => ({
      design_tool_name: tool.design_tool_name, // 手動入力（テキストフィールド）
      design_tool_id: tool.design_tool_id, // 選択式（ドロップダウン）の場合のID
    })) || [{ design_tool_name: "", design_tool_id: "" }],

    tools: workHistory.work_histories_tools?.map((tool) => ({
      tool_name: tool.tool_name, // 手動入力（テキストフィールド）
      tool_id: tool.tool_id, // 選択式（ドロップダウン）の場合のID
    })) || [{ tool_name: "", tool_id: "" }],

    // ========================================
    // ステップ13-18: その他技術要素（選択式用）
    // ========================================
    // 選択式フィールド - 文字列配列形式（チェックボックス・マルチセレクト用）
    selectedOperatingSystems:
      workHistory.work_histories_operating_systems?.map(
        (os) => os.operating_system_name
      ) || [],
    selectedMiddlewares:
      workHistory.work_histories_middlewares?.map((mw) => mw.middleware_name) ||
      [],
    selectedDbs: workHistory.work_histories_dbs?.map((db) => db.db_name) || [],
    selectedNetworks:
      workHistory.work_histories_networks?.map(
        (network) => network.network_name
      ) || [],
    selectedDesignTools:
      workHistory.work_histories_design_tools?.map(
        (tool) => tool.design_tool_name
      ) || [],
    selectedTools:
      workHistory.work_histories_tools
        ?.map((tool) => tool.tool_name)
        .filter((name) => name && name.trim() !== "") || [],
  };

  return formData;
};
